/*
 * MSAL sign-in helper for the admin portal. No-ops cleanly when AUTH_ENABLED is
 * false (local dev), so the rest of the app doesn't need to branch.
 */

/* global location */
import { PublicClientApplication } from "@azure/msal-browser";
import { AUTH_ENABLED, TENANT_ID, ADMIN_CLIENT_ID, API_SCOPE } from "./authConfig";

let msal = null;

/** Initialize MSAL and resolve any redirect. Returns { enabled, account }. */
export async function initAuth() {
  if (!AUTH_ENABLED) return { enabled: false, account: null };

  msal = new PublicClientApplication({
    auth: {
      clientId: ADMIN_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: `${location.origin}/admin.html`,
    },
    cache: { cacheLocation: "sessionStorage" },
  });

  await msal.initialize();
  const redirect = await msal.handleRedirectPromise();
  if (redirect && redirect.account) msal.setActiveAccount(redirect.account);

  const account = msal.getActiveAccount() || msal.getAllAccounts()[0] || null;
  if (account) msal.setActiveAccount(account);
  return { enabled: true, account };
}

export function login() {
  return msal.loginRedirect({ scopes: [API_SCOPE] });
}

export function logout() {
  return msal.logoutRedirect();
}

export function getAccountName() {
  const a = msal && msal.getActiveAccount();
  return a ? a.name || a.username : null;
}

/** Signed-in user's email/UPN (for previewing signatures with real Graph data), or null. */
export function getSignedInEmail() {
  const a = msal && msal.getActiveAccount();
  return a ? a.username || null : null;
}

/** Bearer access token for the admin API, or null when auth is disabled. */
export async function getToken() {
  if (!AUTH_ENABLED) return null;
  const account = msal.getActiveAccount();
  if (!account) throw new Error("Not signed in");
  try {
    const res = await msal.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return res.accessToken;
  } catch {
    // Silent failed (consent/expired) — fall back to an interactive redirect.
    await msal.acquireTokenRedirect({ scopes: [API_SCOPE], account });
    return null;
  }
}
